// THE WIRE (documented in docs/NET.md). Colyseus messages, msgpack-encoded by Colyseus; payloads here are compact
// arrays of integers so a snapshot of 36 bodies stays a few hundred bytes.
//
// ANTI-CHEAT, by construction: the PUBLIC part of a snapshot is encoded once per tick and sent byte-identical to every
// client. It is built only from Sim.publicView(), where Sneaks and townsfolk carry the same fields, the same costume
// indices, random ids, rows sorted by id. The only marks a body can carry are the public tells (blunder "?", glint) and,
// once caught, the reveal. The PRIVATE part goes to one client and only ever describes that client's own seat.
// The Inspector's identity IS public (its id + seat are in the header).
import { ANIMS, type Anim, type PublicView, type PrivateView, type SimEvent, type Cosmetics, type Phase, type Role, type BusState } from "./sim";
import { CALLS, type Call, SIM_HZ } from "./tuning";

export const PROTOCOL_VERSION = 1;
export const ROOM_NAME = "town";

// ---------------------------------------------------------------------------------------------------------------------
// client → server

/** joinOrCreate("town", JoinOptions) for the public quick-join; joinById(roomId, JoinOptions) for a private code */
export type JoinOptions = {
  v?: number;                 // PROTOCOL_VERSION the client speaks
  name?: string;              // display name (filtered server-side)
  pid?: string;               // profile id (the weekly board key)
  cos?: Partial<Cosmetics>;   // mask / colour / reveal fx (Sneak), hat / ray (Inspector) — never the costume
  first?: boolean;            // first visit ever: the next round at this table is an onboarding round
  public?: boolean;           // set by the client on quick-join (private rooms are made via POST /r)
  plat?: string; build?: string;   // where the client came from (cg | web | ...) and which build, for the logs
};
/** "i": one per client sim tick (SIM_HZ). s = sequence (acked in the private snapshot for reconciliation) */
export type InputMsg = { s: number; mx: number; mz: number; sp: 0 | 1; a: 0 | 1 };
/** "pop": target body id + aim point [x, y, z] (where the camera ray passed closest to that body's axis).
 *  Touch: send "mark" on the tap, then "pop" with no p to fire at the mark. */
export type PopMsg = { id: number; p?: [number, number, number] };
export type MarkMsg = { id: number; p: [number, number, number] };
/** "whistle": the Inspector's fake call (c = which call to fake; omitted = random, never FREEZE) */
export type WhistleMsg = { c?: Call };
/** "cos": change cosmetics between rounds; "name": change display name */
export type CosMsg = Partial<Cosmetics>;

// ---------------------------------------------------------------------------------------------------------------------
// server → client

/** "seat": sent once on join (and again after a reconnect) */
export type SeatMsg = { v: number; seat: number; code: string; roomId: string; name: string };

/** body row stride and field order in PublicSnap.e */
export const ROW = 8;
export const F = { id: 0, x: 1, z: 2, h: 3, anim: 4, age: 5, costume: 6, flags: 7 } as const;
/** public body flags (the ONLY per-body marks on the wire) */
export const FLAG = { blunder: 1, glint: 2, revealed: 4, inspector: 8 } as const;
const PHASES: Phase[] = ["intro", "play", "results"];
const BUS_STATES: BusState[] = ["away", "arriving", "docked", "leaving"];

export type PublicSnap = {
  k: number;                  // server tick
  ph: number; pl: number;     // phase index (intro/play/results), phase seconds left ×10
  n: number;                  // round number
  v: number; vg: number;      // vault, vault goal
  fl: number;                 // 1 parade · 2 onboarding round
  w: number;                  // winner: 0 none · 1 Sneaks · 2 Inspector
  c: number[];                // call [callIndex, ageTicks] or []
  wh: number[];               // whistle [callIndex, ageTicks, inspectorId] or []
  st: number;                 // stalls holding a capsule (bit i = stall i)
  g: number;                  // Golden Capsule: 0 none · 1 on the float · 2 carried · 3 stashed
  f: number[];                // float [x cm, z cm, heading byte] or []
  pz: number[];               // panic zones, flat [x cm, z cm, left ×10, ...]
  bs: number[];               // bus [stateIndex, ageTicks]
  i: number; is: number; ch: number;   // the Inspector: body id, seat, charges (public: "He has 2 POPS left")
  ic: number[];               // the Inspector's cosmetics [hat, ray] (public: everyone sees his hat and ray)
  nx: number;                 // next round's Inspector seat (during results), else -1
  rv: number[][];             // reveals of caught Sneaks still on screen: [id, mask, colour, fx]
  e: number[];                // bodies, flat, ROW numbers each (see F), sorted by id
  ro?: [string, 0 | 1, 0 | 1][];  // roster (name, human, isInspector) — sent when it changes and every 2 s
};
export type PrivateSnap = {
  s: number; r: 0 | 1; me: number; ack: number;   // seat, role (0 Sneak, 1 Inspector), own body id (0 = none), last input seq applied
  x: number; z: number; h: number;               // own body (cm, cm, heading byte) — the reconciliation anchor
  lk: 0 | 1;                                     // locked: performing a call / respawning / not in play — don't predict
  ca: number; gp: number; sp: number;            // carrying (0/1/2 golden), grab / stash progress 0–100
  cn: number;                                    // what ACTION does now: 0 nothing · 1 grab · 2 stash · 3 answer · 4 freeze (stand still)
  ri: number;                                    // respawn in ×10 (-1 = not respawning)
  an: number;                                    // this call: 0 pending · 1 answered · 2 PERFECT · 3 blundered
  fz: 0 | 1;                                     // holding a FREEZE (any move = blunder)
  chg: number; cd: number; wi: number; rg: number; mk: number;   // Inspector: charges, cooldown ×10, whistle ready in ×10, regen in ×10, mark id
  ss: number; pf: number; ct: number; ni: 0 | 1; // this round: stashes, perfects, catches; you are next round's Inspector
};
export type Snap = { p: PublicSnap; m: PrivateSnap };
/** "ev": events, public ones to everyone, private ones only to their seat (the `to` field is stripped on the wire) */
export type WireEvent = DistributiveOmit<SimEvent, "to">;
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

// ---------------------------------------------------------------------------------------------------------------------
// encoding

export const qPos = (v: number) => Math.round(v * 100);
export const qHead = (h: number) => ((Math.round((h / (2 * Math.PI)) * 256) % 256) + 256) % 256;
export const dqPos = (v: number) => v / 100;
export const dqHead = (b: number) => { const h = (b / 256) * 2 * Math.PI; return h > Math.PI ? h - 2 * Math.PI : h; };
const ticks = (sec: number) => Math.max(0, Math.round(sec * SIM_HZ));
const tenths = (sec: number) => Math.round(sec * 10);

export function encodePublic(pub: PublicView, withRoster: boolean): PublicSnap {
  const e: number[] = [];
  for (const b of pub.bodies) {
    const flags = (b.blunder ? FLAG.blunder : 0) | (b.glint ? FLAG.glint : 0) | (b.revealed ? FLAG.revealed : 0) | (b.inspector ? FLAG.inspector : 0);
    e.push(b.id, qPos(b.x), qPos(b.z), qHead(b.h), ANIMS.indexOf(b.anim), Math.min(255, ticks(b.age)), b.costume, flags);
  }
  const snap: PublicSnap = {
    k: pub.tick, ph: PHASES.indexOf(pub.phase), pl: Math.max(0, tenths(pub.phaseLeft)), n: pub.round,
    v: pub.vault, vg: pub.goal, fl: (pub.parade ? 1 : 0) | (pub.onboarding ? 2 : 0), w: pub.winner === "sneak" ? 1 : pub.winner === "inspector" ? 2 : 0,
    c: pub.call ? [CALLS.indexOf(pub.call.kind), ticks(pub.call.age)] : [],
    wh: pub.whistle ? [CALLS.indexOf(pub.whistle.kind), ticks(pub.whistle.age), pub.whistle.by] : [],
    st: pub.stalls.reduce((m, h, i) => m | (h ? 1 << i : 0), 0), g: pub.golden,
    f: pub.float ? [qPos(pub.float.x), qPos(pub.float.z), qHead(pub.float.rot)] : [],
    pz: pub.panics.flatMap((p) => [qPos(p.x), qPos(p.z), tenths(p.left)]),
    bs: [BUS_STATES.indexOf(pub.bus.state), Math.min(65535, ticks(pub.bus.age))],
    i: pub.inspector, is: pub.inspectorSeat, ch: pub.charges, nx: pub.nextInspectorSeat, ic: [pub.inspectorCos.hat, pub.inspectorCos.ray],
    rv: pub.reveals.map((r) => [r.id, r.cos.mask, r.cos.colour, r.cos.fx]),
    e,
  };
  if (withRoster) snap.ro = pub.seats.map((s, i) => [s.name, s.human ? 1 : 0, i === pub.inspectorSeat ? 1 : 0]);
  return snap;
}

const CAN = ["", "grab", "stash", "answer", "freeze"] as const;
export function encodePrivate(p: PrivateView, ack: number): PrivateSnap {
  return {
    s: p.seat, r: p.role === "inspector" ? 1 : 0, me: p.me, ack,
    x: qPos(p.x), z: qPos(p.z), h: qHead(p.h), lk: p.locked ? 1 : 0,
    ca: p.carry, gp: Math.round(Math.min(1, p.grab) * 100), sp: Math.round(Math.min(1, p.stash) * 100), cn: CAN.indexOf(p.can),
    ri: p.respawnIn >= 0 ? tenths(p.respawnIn) : -1, an: p.answered, fz: p.freezeHold ? 1 : 0,
    chg: p.charges, cd: tenths(p.cooldown), wi: tenths(p.whistleIn), rg: p.regenIn >= 0 ? tenths(p.regenIn) : -1, mk: p.mark,
    ss: p.stashes, pf: p.perfects, ct: p.catches, ni: p.nextInspector ? 1 : 0,
  };
}
/** the event kinds that only ever go to one seat (the wire strips their `to`; a client re-attaches its own seat) */
export const PRIVATE_EVENTS = new Set(["answer", "blundered", "pop_reject", "grabbed", "stash", "respawn", "caught", "marked", "results"]);
/** a SimEvent as it goes on the wire (the routing field removed) */
export function wireEvent(e: SimEvent): WireEvent { if (!("to" in e)) return e; const { to: _to, ...rest } = e as SimEvent & { to: number }; return rest as WireEvent; }

// ---------------------------------------------------------------------------------------------------------------------
// decoding (the client)

export type BodyState = { id: number; x: number; z: number; h: number; anim: Anim; age: number; costume: number; blunder: boolean; glint: boolean; revealed: boolean; inspector: boolean };
export type HeaderState = {
  tick: number; phase: Phase; phaseLeft: number; round: number; vault: number; goal: number; parade: boolean; onboarding: boolean;
  winner: Role | ""; call: { kind: Call; age: number } | null; whistle: { kind: Call; age: number; by: number } | null;
  stalls: boolean[]; golden: number; float: { x: number; z: number; rot: number } | null; panics: { x: number; z: number; left: number }[];
  bus: { state: BusState; age: number }; inspector: number; inspectorSeat: number; charges: number; nextInspectorSeat: number;
  reveals: Map<number, { mask: number; colour: number; fx: number }>;
  inspectorCos: { hat: number; ray: number };
};
export type MeState = {
  seat: number; role: Role; me: number; ack: number; x: number; z: number; h: number; locked: boolean;
  carry: number; grab: number; stash: number; can: typeof CAN[number]; respawnIn: number; answered: number; freezeHold: boolean;
  charges: number; cooldown: number; whistleIn: number; regenIn: number; mark: number;
  stashes: number; perfects: number; catches: number; nextInspector: boolean;
};

export function decodePublic(s: PublicSnap): { head: HeaderState; bodies: BodyState[] } {
  const bodies: BodyState[] = [];
  for (let k = 0; k + ROW <= s.e.length; k += ROW) {
    const fl = s.e[k + F.flags];
    bodies.push({
      id: s.e[k + F.id], x: dqPos(s.e[k + F.x]), z: dqPos(s.e[k + F.z]), h: dqHead(s.e[k + F.h]), anim: ANIMS[s.e[k + F.anim]] || "idle",
      age: s.e[k + F.age] / SIM_HZ, costume: s.e[k + F.costume],
      blunder: !!(fl & FLAG.blunder), glint: !!(fl & FLAG.glint), revealed: !!(fl & FLAG.revealed), inspector: !!(fl & FLAG.inspector),
    });
  }
  const head: HeaderState = {
    tick: s.k, phase: PHASES[s.ph] || "intro", phaseLeft: s.pl / 10, round: s.n, vault: s.v, goal: s.vg, parade: !!(s.fl & 1), onboarding: !!(s.fl & 2),
    winner: s.w === 1 ? "sneak" : s.w === 2 ? "inspector" : "",
    call: s.c.length ? { kind: CALLS[s.c[0]], age: s.c[1] / SIM_HZ } : null,
    whistle: s.wh.length ? { kind: CALLS[s.wh[0]], age: s.wh[1] / SIM_HZ, by: s.wh[2] } : null,
    stalls: Array.from({ length: 6 }, (_, i) => !!(s.st & (1 << i))), golden: s.g,
    float: s.f.length ? { x: dqPos(s.f[0]), z: dqPos(s.f[1]), rot: dqHead(s.f[2]) } : null,
    panics: Array.from({ length: s.pz.length / 3 }, (_, i) => ({ x: dqPos(s.pz[i * 3]), z: dqPos(s.pz[i * 3 + 1]), left: s.pz[i * 3 + 2] / 10 })),
    bus: { state: BUS_STATES[s.bs[0]] || "away", age: s.bs[1] / SIM_HZ },
    inspector: s.i, inspectorSeat: s.is, charges: s.ch, nextInspectorSeat: s.nx,
    reveals: new Map(s.rv.map(([id, mask, colour, fx]) => [id, { mask, colour, fx }])),
    inspectorCos: { hat: s.ic?.[0] ?? 0, ray: s.ic?.[1] ?? 0 },
  };
  return { head, bodies };
}
export function decodePrivate(m: PrivateSnap): MeState {
  return {
    seat: m.s, role: m.r ? "inspector" : "sneak", me: m.me, ack: m.ack, x: dqPos(m.x), z: dqPos(m.z), h: dqHead(m.h), locked: !!m.lk,
    carry: m.ca, grab: m.gp / 100, stash: m.sp / 100, can: CAN[m.cn] || "", respawnIn: m.ri < 0 ? -1 : m.ri / 10, answered: m.an, freezeHold: !!m.fz,
    charges: m.chg, cooldown: m.cd / 10, whistleIn: m.wi / 10, regenIn: m.rg < 0 ? -1 : m.rg / 10, mark: m.mk,
    stashes: m.ss, perfects: m.pf, catches: m.ct, nextInspector: !!m.ni,
  };
}

// ---------------------------------------------------------------------------------------------------------------------
// validation of what clients send (the server trusts nothing)

const num = (v: unknown, lo: number, hi: number) => { const n = Number(v); return Number.isFinite(n) ? Math.max(lo, Math.min(hi, n)) : 0; };
export function cleanInput(m: unknown): InputMsg | null {
  if (!m || typeof m !== "object") return null;
  const o = m as Record<string, unknown>;
  return { s: Math.max(0, Math.floor(num(o.s, 0, 2 ** 31))), mx: num(o.mx, -1, 1), mz: num(o.mz, -1, 1), sp: o.sp ? 1 : 0, a: o.a ? 1 : 0 };
}
export function cleanVec3(p: unknown): { x: number; y: number; z: number } | null {
  if (!Array.isArray(p) || p.length !== 3) return null;
  const [x, y, z] = p.map(Number); if (![x, y, z].every(Number.isFinite)) return null;
  if (Math.abs(x) > 100 || Math.abs(y) > 100 || Math.abs(z) > 100) return null;
  return { x, y, z };
}
export function cleanCall(c: unknown): Call | undefined { return typeof c === "string" && (CALLS as readonly string[]).includes(c) ? (c as Call) : undefined; }
export function cleanCos(c: unknown): Cosmetics {
  const o = (c && typeof c === "object" ? c : {}) as Record<string, unknown>;
  const k = (v: unknown, max: number) => Math.max(0, Math.min(max, Math.floor(Number(v) || 0)));
  return { mask: k(o.mask, 7), colour: k(o.colour, 7), fx: k(o.fx, 5), hat: k(o.hat, 5), ray: k(o.ray, 5) };
}
